import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Search, MoreVertical, Loader2, Plus, AlertCircle, Edit2, Trash2, X,
  TrendingUp, BarChart3, Clock, DollarSign, User, Calendar
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from '../lib/axios';
import { getDealsPipeline, moveDealStage, createDeal, deleteDeal } from '../services/apiDeals';
import { searchContacts } from '../services/apiContacts';
import { useAuthStore } from '../store/useAuthStore';
import { CURRENCIES, currencyOptionLabel, tenantDefaultCurrency } from '../lib/currencies';
import StrictModeDroppable from '../components/common/StrictModeDroppable';
import { resolveMediaUrl } from '../lib/utils';

// Stage configuration mapping for human-friendly terms and colors
const STAGE_CONFIGS = {
  prospecting: { name: 'Prospección', color: '#64748b' },
  qualification: { name: 'Calificación', color: '#3b82f6' },
  proposal: { name: 'Propuesta', color: '#8b5cf6' },
  negotiation: { name: 'Negociación', color: '#f59e0b' },
  closed_won: { name: 'Ganado', color: '#10b981' },
  closed_lost: { name: 'Perdido', color: '#ef4444' }
};

export default function DealsBoard() {
  const { user, tenant } = useAuthStore();
  const defaultCurrency = tenantDefaultCurrency(tenant);
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [isAddingDeal, setIsAddingDeal] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  
  // Deal Form State
  const [newDealForm, setNewDealForm] = useState({
    title: '',
    value: '',
    currency: 'USD',
    stage: 'prospecting',
    contact_id: '',
    expected_close_date: '',
  });

  const loadPipeline = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getDealsPipeline();
      setPipeline(data.pipeline || []);
      setErrorMsg('');
    } catch (e) {
      console.error(e);
      setErrorMsg('No se pudo cargar el embudo de negocios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const dealId = Number(draggableId);
    const fromStage = source.droppableId;
    const toStage = destination.droppableId;

    // Optimistic local state update
    const nextPipeline = pipeline.map(col => {
      if (col.stage === fromStage) {
        const nextDeals = [...col.deals];
        const index = nextDeals.findIndex(d => d.id === dealId);
        if (index !== -1) {
          const [movedDeal] = nextDeals.splice(index, 1);
          return {
            ...col,
            deals: nextDeals,
            count: col.count - 1,
            total_value: Number(col.total_value) - Number(movedDeal.value || 0)
          };
        }
      }
      return col;
    });

    const movedDealObj = pipeline.find(col => col.stage === fromStage)?.deals.find(d => d.id === dealId);
    
    const finalPipeline = nextPipeline.map(col => {
      if (col.stage === toStage && movedDealObj) {
        const updatedDeal = { ...movedDealObj, stage: toStage };
        const nextDeals = [...col.deals];
        nextDeals.splice(destination.index, 0, updatedDeal);
        return {
          ...col,
          deals: nextDeals,
          count: col.count + 1,
          total_value: Number(col.total_value) + Number(updatedDeal.value || 0)
        };
      }
      return col;
    });

    setPipeline(finalPipeline);
    setSavingId(dealId);

    try {
      await moveDealStage(dealId, toStage);
      setErrorMsg('');
    } catch (err) {
      console.error(err);
      setErrorMsg('Error al guardar el cambio de etapa. Reabriendo...');
      loadPipeline();
    } finally {
      setSavingId(null);
    }
  };

  // Contacts Autocomplete for Modal
  useEffect(() => {
    if (contactSearch.length < 2) {
      setContactResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setSearchingContacts(true);
        const contacts = await searchContacts(contactSearch);
        setContactResults(contacts || []);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchingContacts(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [contactSearch]);

  const handleCreateDeal = async (e) => {
    e.preventDefault();
    if (!newDealForm.title.trim()) return;

    try {
      await createDeal({
        ...newDealForm,
        value: newDealForm.value ? parseFloat(newDealForm.value) : 0
      });
      setIsAddingDeal(false);
      setNewDealForm({
        title: '',
        value: '',
        currency: defaultCurrency,
        stage: 'prospecting',
        contact_id: '',
        expected_close_date: '',
      });
      setContactSearch('');
      loadPipeline();
    } catch (err) {
      console.error(err);
      alert('Error al crear el negocio.');
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!confirm('¿Estás seguro de eliminar este negocio?')) return;
    try {
      await deleteDeal(dealId);
      loadPipeline();
    } catch (err) {
      console.error(err);
      alert('No se pudo eliminar el negocio.');
    }
  };

  // Format currency helper
  const formatCurrency = (val, curr = 'USD') => {
    try {
      return new Intl.NumberFormat('es-US', {
        style: 'currency',
        currency: curr,
        minimumFractionDigits: 2
      }).format(val);
    } catch {
      // Intl lanza RangeError con un código de moneda que no reconoce. Un dato
      // viejo o mal cargado no debe tumbar el tablero entero: se muestra el
      // número con el código al lado y listo.
      return `${curr} ${Number(val ?? 0).toFixed(2)}`;
    }
  };

  if (loading && pipeline.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="animate-spin text-primary" size={40} />
          <p className="font-semibold text-slate-400">Cargando embudo de ventas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6 md:p-8 gap-6 overflow-hidden relative">
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 shrink-0">
        <div className="flex-1">
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white flex items-center gap-3">
            Negociaciones
            <span className="text-[10px] leading-none bg-emerald-500/10 text-emerald-500 px-3 py-1.5 rounded-full border border-emerald-500/20 uppercase tracking-[0.2em] font-black">
              CRM Deals
            </span>
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Administra tus oportunidades de venta y realiza seguimiento a tus transacciones comerciales.
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {errorMsg && (
            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-400/10 text-red-600 dark:text-red-400 text-xs px-4 py-2.5 rounded-xl border border-red-100 dark:border-red-500/20 max-w-xs animate-pulse">
              <AlertCircle size={15} className="shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <input
              type="text"
              placeholder="Buscar negocio..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 w-52 shadow-sm text-slate-900 dark:text-white placeholder:text-slate-400"
            />
          </div>

          <button
            onClick={() => {
              // La moneda se fija al abrir y no en el useState inicial: el
              // tenant llega de forma asíncrona (checkAuth), así que en el
              // primer render puede no estar todavía.
              setNewDealForm(f => ({ ...f, currency: defaultCurrency }));
              setIsAddingDeal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/95 text-white rounded-xl shadow-lg shadow-primary/20 transition-all font-bold text-sm"
          >
            <Plus size={16} />
            <span>Nuevo Negocio</span>
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex-1 overflow-x-auto overflow-y-hidden pb-6 custom-scrollbar">
          <div className="flex gap-4 h-full items-start" style={{ minWidth: '110rem' }}>
            
            {pipeline.map(col => {
              const config = STAGE_CONFIGS[col.stage] || { name: col.stage, color: '#94a3b8' };
              
              // Local search filter
              let visibleDeals = col.deals || [];
              if (searchQuery) {
                visibleDeals = visibleDeals.filter(d => 
                  d.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                  (d.contact?.first_name && d.contact.first_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                  (d.contact?.last_name && d.contact.last_name.toLowerCase().includes(searchQuery.toLowerCase()))
                );
              }

              return (
                <div
                  key={col.stage}
                  className="w-72 shrink-0 flex flex-col rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200/50 dark:border-white/5"
                  style={{ maxHeight: 'calc(100vh - 240px)' }}
                >
                  
                  {/* Column Header */}
                  <div className="px-4 py-3.5 border-b border-slate-200/50 dark:border-white/5 flex flex-col gap-1 shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: config.color }} />
                        <span className="text-xs font-black uppercase tracking-[0.1em] text-slate-700 dark:text-white/80">
                          {config.name}
                        </span>
                        <span className="text-[10px] font-bold bg-slate-200 dark:bg-white/10 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded-md">
                          {visibleDeals.length}
                        </span>
                      </div>
                    </div>
                    {/* Sum Value */}
                    <div className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1">
                      <DollarSign size={12} className="text-slate-400" />
                      <span>{formatCurrency(col.total_value)}</span>
                    </div>
                  </div>

                  {/* Column Body */}
                  <StrictModeDroppable droppableId={col.stage}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 overflow-y-auto px-3 py-3 space-y-3 no-scrollbar transition-colors ${
                          snapshot.isDraggingOver ? 'bg-primary/[0.02] dark:bg-primary/[0.01]' : ''
                        }`}
                        style={{ minHeight: 180 }}
                      >
                        
                        {visibleDeals.map((deal, index) => (
                          <Draggable key={deal.id} draggableId={String(deal.id)} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`p-4 bg-white dark:bg-surface-dark rounded-xl border relative shadow-sm transition-all ${
                                  snapshot.isDragging 
                                    ? 'border-primary shadow-2xl ring-2 ring-primary/10 rotate-1 cursor-grabbing'
                                    : 'border-slate-100 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10 hover:shadow-md cursor-grab'
                                }`}
                              >
                                {savingId === deal.id && (
                                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/60 dark:bg-black/50 backdrop-blur-[1px]">
                                    <Loader2 className="animate-spin text-primary" size={18} />
                                  </div>
                                )}

                                <div className="flex justify-between items-start gap-2 mb-2">
                                  <h4 className="text-sm font-bold text-slate-900 dark:text-white line-clamp-1">
                                    {deal.title}
                                  </h4>
                                  <button
                                    onClick={() => handleDeleteDeal(deal.id)}
                                    className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                    title="Eliminar negocio"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>

                                <div className="text-xs font-black text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-0.5">
                                  <span>{formatCurrency(deal.value, deal.currency)}</span>
                                </div>

                                {/* Contact Link */}
                                {deal.contact ? (
                                  <div className="flex items-center gap-1.5 mb-3 bg-slate-50 dark:bg-white/[0.02] p-2 rounded-lg border border-slate-100 dark:border-white/5">
                                    <User size={12} className="text-slate-400 shrink-0" />
                                    <Link
                                      to={`/contacts/${deal.contact.id}/360`}
                                      className="text-xs font-semibold text-primary hover:underline truncate"
                                    >
                                      {deal.contact.first_name} {deal.contact.last_name}
                                    </Link>
                                  </div>
                                ) : (
                                  <div className="text-xs text-slate-400 mb-3 italic">Sin contacto asignado</div>
                                )}

                                {/* Card Footer */}
                                <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-white/5 text-[10px] text-slate-400">
                                  <div className="flex items-center gap-1">
                                    <Clock size={10} />
                                    <span>{new Date(deal.created_at).toLocaleDateString()}</span>
                                  </div>
                                  
                                  {/* Owner Avatar */}
                                  <div
                                    className="w-5 h-5 rounded-full bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/15 flex items-center justify-center overflow-hidden"
                                    title={deal.owner?.name ? `Propietario: ${deal.owner.name}` : 'Sin propietario'}
                                  >
                                    {deal.owner?.avatar ? (
                                      <img src={resolveMediaUrl(deal.owner.avatar)} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <User size={9} className="text-slate-400" />
                                    )}
                                  </div>
                                </div>

                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </StrictModeDroppable>

                </div>
              );
            })}

          </div>
        </div>
      </DragDropContext>

      {/* Add Deal Modal */}
      {isAddingDeal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white">Nuevo Negocio</h3>
              <button 
                onClick={() => setIsAddingDeal(false)} 
                className="p-1.5 hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg text-slate-400"
              >
                <X size={18} />
              </button>
            </div>
            
            <form onSubmit={handleCreateDeal} className="p-6 space-y-4">
              
              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  Título del Negocio *
                </label>
                <input
                  type="text"
                  value={newDealForm.title}
                  onChange={e => setNewDealForm({ ...newDealForm, title: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white"
                  placeholder="Ej. Compra Granito - Mármolgres"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Valor
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newDealForm.value}
                    onChange={e => setNewDealForm({ ...newDealForm, value: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Moneda
                  </label>
                  <select
                    value={newDealForm.currency}
                    onChange={e => setNewDealForm({ ...newDealForm, currency: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>{currencyOptionLabel(c)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  Etapa Inicial
                </label>
                <select
                  value={newDealForm.stage}
                  onChange={e => setNewDealForm({ ...newDealForm, stage: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white"
                >
                  {Object.entries(STAGE_CONFIGS).map(([val, conf]) => (
                    <option key={val} value={val}>{conf.name}</option>
                  ))}
                </select>
              </div>

              {/* Contact Autocomplete */}
              <div className="relative">
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  Asociar Contacto
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Buscar contacto por nombre o teléfono..."
                    value={contactSearch}
                    onChange={e => setContactSearch(e.target.value)}
                    className="pl-10 pr-4 py-2.5 w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white"
                  />
                  {searchingContacts && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" size={16} />
                  )}
                </div>

                {contactResults.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 rounded-xl shadow-lg max-h-48 overflow-y-auto z-10">
                    {contactResults.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setNewDealForm({ ...newDealForm, contact_id: c.id });
                          setContactSearch(`${c.first_name} ${c.last_name || ''} (${c.phone})`);
                          setContactResults([]);
                        }}
                        className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 dark:hover:bg-white/5 flex flex-col"
                      >
                        <span className="font-bold text-slate-800 dark:text-white">
                          {c.first_name} {c.last_name || ''}
                        </span>
                        <span className="text-slate-400">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  Fecha Prevista de Cierre
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="date"
                    value={newDealForm.expected_close_date}
                    onChange={e => setNewDealForm({ ...newDealForm, expected_close_date: e.target.value })}
                    className="pl-10 pr-4 py-2.5 w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => setIsAddingDeal(false)}
                  className="px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-bold bg-primary text-white rounded-xl shadow-lg shadow-primary/20 hover:bg-primary/95"
                >
                  Crear Negocio
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}
